/**
 * KioskContext - Shared state and functions for Kiosk components
 * 
 * REAL-TIME MENU SYNC: Auto-refreshes when admin makes changes
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../../utils/apiClient';
import { useMenuSync } from '../../hooks/useMenuSync';

// Country codes for phone input
export const COUNTRY_CODES = [
  { code: '+679', country: 'Fiji', flag: '🇫🇯' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
];

// Default category order (used as fallback, actual categories fetched from API)
export const DEFAULT_CATEGORY_ORDER = [
  'build-your-own', 'coffee', 'iced-drinks', 'smoothies-blends', 'pies',
  'salads', 'pastries', 'breads', 'breakfast', 'lunch', 'milk-shakes',
  'grab-and-go', 'condiments', 'kids', 'fresh-mixed-juice', 'desserts', 
  'sweets', 'muffins'
];

// Category display names (fallback if API doesn't provide them)
export const CATEGORY_LABELS = {
  'build-your-own': 'Build Your Own',
  'coffee': 'Coffee',
  'iced-drinks': 'Iced Drinks',
  'smoothies-blends': 'Smoothies & Blends',
  'pies': 'Pies',
  'salads': 'Salads',
  'pastries': 'Pastries',
  'breads': 'Breads',
  'breakfast': 'Breakfast',
  'lunch': 'Lunch',
  'milk-shakes': 'Milkshakes',
  'grab-and-go': 'Grab & Go',
  'condiments': 'Condiments',
  'kids': 'Kids',
  'fresh-mixed-juice': 'Fresh Mixed Juice',
  'desserts': 'Desserts',
  'sweets': 'Sweets',
  'muffins': 'Muffins'
};

// Logo URL - use local asset for production compatibility
export const BULACCINO_LOGO = "/logo.png";

// Loyalty tier definitions
export const LOYALTY_TIERS = {
  new_member: { discount: 0, name: 'Member', color: 'gray' },
  bronze: { discount: 10, name: 'Bronze', color: 'orange' },
  silver: { discount: 12.5, name: 'Silver', color: 'gray' },
  gold: { discount: 15, name: 'Gold', color: 'amber' },
  platinum: { discount: 20, name: 'Platinum', color: 'purple' }
};

const KioskContext = createContext(null);

export function KioskProvider({ children }) {
  // View state
  const [view, setView] = useState('welcome');
  
  // Menu & Cart state
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]); // Categories from API
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [idleTimeout, setIdleTimeout] = useState(null);
  const [availableAddOns, setAvailableAddOns] = useState([]);
  
  // Customer/Loyalty state
  const [customer, setCustomer] = useState(null);
  const [loyaltyLookup, setLoyaltyLookup] = useState('');
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  
  // Signup form state
  const [signupForm, setSignupForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country_code: '+679'
  });
  const [signupLoading, setSignupLoading] = useState(false);
  
  // Customization modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCustomization, setShowCustomization] = useState(false);
  
  // Partner code state
  const [partnerCode, setPartnerCode] = useState('');
  const [partnerCodeValid, setPartnerCodeValid] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  
  // Coupon code state (separate from partner code)
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(null);
  const [couponInfo, setCouponInfo] = useState(null);
  
  // Coffee Pass redemption state
  const [coffeeClubStatus, setCoffeeClubStatus] = useState(null);
  const [coffeeClubRedemptionWindow, setCoffeeClubRedemptionWindow] = useState(null);
  
  // Payment state
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'paid', 'unpaid', 'cash_pending'
  
  // Upsell popup state
  const [showUpsell, setShowUpsell] = useState(false);
  const [lastAddedItem, setLastAddedItem] = useState(null);
  
  // Idle carousel state
  const [showIdleCarousel, setShowIdleCarousel] = useState(false);
  const idleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  
  // Settings
  const [settings, setSettings] = useState({
    idle_timeout_seconds: 300,
    idle_carousel_enabled: true,
    idle_carousel_delay: 60, // Show carousel after 60 seconds of inactivity
    welcome_message: 'Welcome to Bulaccino Cafe',
    thank_you_message: 'Vinaka! Your order has been placed',
    show_prices: true,
    require_customer_name: false,
    max_items_per_order: 20,
    allowed_categories: [],
    blocked_categories: [],
    theme: 'dark',
    play_sounds: true,
    show_order_number_large: true,
    accessibility_mode: false,
    large_text_size: 'normal',
    touch_target_size: 'normal',
    high_contrast: false,
    upsell_enabled: true
  });
  
  // VAT/Tax settings - Fiji VAT rate is 12.5%
  const [vatRate, setVatRate] = useState(12.5); // 12.5% VAT for Fiji
  
  // Accessibility
  const [accessibilityEnabled, setAccessibilityEnabled] = useState(false);
  const isAccessible = accessibilityEnabled || settings.accessibility_mode;
  
  // Computed accessibility classes
  const buttonSizeClass = isAccessible ? 'min-h-[72px] min-w-[72px]' : 'min-h-[56px] min-w-[56px]';
  const iconButtonClass = isAccessible ? 'w-16 h-16' : 'w-12 h-12';
  const iconClass = isAccessible ? 'w-8 h-8' : 'w-6 h-6';
  const largeIconClass = isAccessible ? 'w-12 h-12' : 'w-8 h-8';

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
    fetchMenuItems();
    fetchCoffeeClubRedemptionWindow();
  }, []);

  // ============== REAL-TIME MENU SYNC ==============
  // Listen for menu changes from admin panel and auto-refresh
  const handleMenuUpdate = useCallback((data) => {
    console.log('[Kiosk] Menu update received:', data);
    
    // Silently refresh menu data without interrupting customer
    fetchMenuItems();
    
    // Only show toast if it's a major update
    if (data.update_type === 'full_refresh') {
      // Subtle notification for kiosk
      console.log('[Kiosk] Full menu refresh triggered');
    }
  }, []);

  // Connect to menu sync WebSocket
  useMenuSync(handleMenuUpdate);

  // Refresh menu when entering ordering views to get latest prices
  useEffect(() => {
    if (view === 'ordering' || view === 'ordering-items') {
      // Background refresh - don't show loading spinner if we already have menu items
      fetchMenuItems(menuItems.length > 0);
    }
  }, [view]);

  // Refresh Coffee Pass redemption window every minute
  useEffect(() => {
    const interval = setInterval(fetchCoffeeClubRedemptionWindow, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Coffee Pass status when customer is selected
  useEffect(() => {
    if (customer?.email || customer?.phone) {
      fetchCoffeeClubStatus(customer.email, customer.phone);
    } else {
      setCoffeeClubStatus(null);
    }
  }, [customer?.email, customer?.phone]);

  // Idle carousel handler - show after inactivity
  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      
      // Hide carousel on any activity
      if (showIdleCarousel) {
        setShowIdleCarousel(false);
      }
      
      // Clear and reset the idle timer
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      
      // Only set idle timer if on welcome screen and carousel is enabled
      if (view === 'welcome' && settings.idle_carousel_enabled) {
        idleTimerRef.current = setTimeout(() => {
          setShowIdleCarousel(true);
        }, (settings.idle_carousel_delay || 60) * 1000);
      }
    };
    
    // Set up activity listeners
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('mousemove', handleActivity);
    
    // Initial timer setup
    handleActivity();
    
    return () => {
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [view, settings.idle_carousel_enabled, settings.idle_carousel_delay, showIdleCarousel]);

  // Idle timeout handler (reset to welcome)
  useEffect(() => {
    let currentIdleTimeout = null;
    const resetIdle = () => {
      if (currentIdleTimeout) clearTimeout(currentIdleTimeout);
      currentIdleTimeout = setTimeout(() => {
        if (view !== 'confirmation' && view !== 'welcome') {
          resetKiosk();
        }
      }, settings.idle_timeout_seconds * 1000);
      setIdleTimeout(currentIdleTimeout);
    };
    
    window.addEventListener('touchstart', resetIdle);
    window.addEventListener('click', resetIdle);
    resetIdle();
    
    return () => {
      window.removeEventListener('touchstart', resetIdle);
      window.removeEventListener('click', resetIdle);
      if (currentIdleTimeout) clearTimeout(currentIdleTimeout);
    };
  }, [settings.idle_timeout_seconds, view]);

  // Validate partner code with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (partnerCode) {
        validatePartnerCode(partnerCode);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [partnerCode]);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/kiosk/settings');
      setSettings(prev => ({ ...prev, ...res.data }));
      if (res.data.accessibility_mode) {
        setAccessibilityEnabled(true);
      }
      
      // Fetch receipt settings to get VAT rate
      const receiptRes = await api.get('/receipts/settings');
      if (receiptRes.data.vat_rate !== undefined) {
        setVatRate(receiptRes.data.vat_rate);
      }
    } catch (err) {
      console.error('Failed to fetch kiosk settings:', err);
    }
  };

  const fetchMenuItems = async (isBackgroundRefresh = false) => {
    try {
      // Only show loading spinner on initial load, not background refreshes
      if (!isBackgroundRefresh && menuItems.length === 0) {
        setLoading(true);
      }
      
      // Fetch menu items
      const res = await api.get('/menu');
      const availableItems = res.data.filter(item => item.is_available !== false);
      setMenuItems(availableItems);
      
      // Fetch categories from API (dynamic, not hardcoded!)
      try {
        const catRes = await api.get('/menu/categories');
        if (catRes.data && catRes.data.length > 0) {
          // Use the order from the API (set by admin), fallback to default order
          setMenuCategories(catRes.data.map(cat => ({
            id: cat.id,
            label: cat.name || CATEGORY_LABELS[cat.id] || cat.id,
            sort_order: cat.order ?? DEFAULT_CATEGORY_ORDER.indexOf(cat.id)
          })).sort((a, b) => {
            // Sort by the order field from database (admin-controlled)
            return (a.sort_order ?? 999) - (b.sort_order ?? 999);
          }));
        }
      } catch (catErr) {
        console.error('Failed to fetch categories:', catErr);
        // Fallback: build categories from menu items
        const uniqueCats = [...new Set(availableItems.map(item => item.category))];
        setMenuCategories(uniqueCats.map(catId => ({
          id: catId,
          label: CATEGORY_LABELS[catId] || catId
        })));
      }
      
      const addOnsRes = await api.get('/add-ons');
      setAvailableAddOns(addOnsRes.data.filter(a => a.is_available !== false));
    } catch (err) {
      console.error('Failed to fetch menu:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh add-ons periodically (every 60 seconds) to pick up admin changes
  const refreshAddOns = async () => {
    try {
      const cacheBuster = `?_t=${Date.now()}`;
      const addOnsRes = await api.get(`/add-ons${cacheBuster}`);
      setAvailableAddOns(addOnsRes.data.filter(a => a.is_available !== false));
    } catch (err) {
      console.error('Failed to refresh add-ons:', err);
    }
  };

  // Auto-refresh add-ons every 60 seconds on Kiosk
  useEffect(() => {
    const interval = setInterval(refreshAddOns, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Coffee Pass redemption window status
  const fetchCoffeeClubRedemptionWindow = async () => {
    try {
      const res = await api.get('/coffee-pass/eligible-items');
      setCoffeeClubRedemptionWindow({
        is_open: res.data.is_window_open,
        message: res.data.window_message,
        window_hours: res.data.redemption_window
      });
    } catch (err) {
      console.error('Failed to fetch coffee club window:', err);
    }
  };

  // Fetch customer's Coffee Pass status using the new API
  const fetchCoffeeClubStatus = async (email, phone) => {
    if (!email && !phone) {
      setCoffeeClubStatus(null);
      return;
    }
    try {
      const params = new URLSearchParams();
      if (email) params.append('email', email);
      if (phone) params.append('phone', phone);
      
      // Use the new coffee-pass/check endpoint
      const res = await api.get(`/coffee-pass/check?${params}`);
      setCoffeeClubStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch coffee club status:', err);
      setCoffeeClubStatus(null);
    }
  };

  const resetKiosk = () => {
    setCart([]);
    setCustomer(null);
    setOrderNumber('');
    setView('welcome');
    setSearch('');
    setActiveCategory('all');
    setLoyaltyLookup('');
    setLookupResults([]);
    setSignupForm({ first_name: '', last_name: '', email: '', phone: '', country_code: '+679' });
    setPartnerCode('');
    setPartnerCodeValid(null);
    setPartnerInfo(null);
    setCouponCode('');
    setCouponValid(null);
    setCouponInfo(null);
    setCoffeeClubStatus(null);
    setShowUpsell(false);
    setLastAddedItem(null);
    setPaymentStatus(null);
  };

  const handleItemClick = (item) => {
    console.log('[KioskContext] handleItemClick called with:', item?.name, item?.id);
    
    // Check if item should skip customization (like Coffee Pass)
    const skipCustomization = item.skip_customization || 
                              item.name?.toLowerCase().includes('bula coffee pass') ||
                              item.name?.toLowerCase().includes('coffee pass') ||
                              item.category?.toLowerCase() === 'coffee pass' ||
                              item.category?.toLowerCase() === 'memberships';
    
    if (skipCustomization) {
      console.log('[KioskContext] Skipping customization - adding directly to cart');
      // Add directly to cart without customization
      // Use BASE item.price - not sizes (sizes are upsells)
      const cartItem = {
        item_id: item.id || item._id,
        name: item.name,
        quantity: 1,
        price: item.price || 95, // Default to $95 for Coffee Pass (VAT-inclusive)
        selectedSize: null, // No size selected - using base price
        image: item.image,
        is_coffee_pass: true
      };
      handleAddCustomizedItem(cartItem);
      return;
    }
    
    console.log('[KioskContext] Setting selectedItem and showCustomization=true');
    setSelectedItem(item);
    setShowCustomization(true);
  };

  const handleAddCustomizedItem = (cartItem) => {
    setCart(prev => {
      // Special handling for Coffee Pass - track for gifting
      if (cartItem.is_coffee_pass) {
        // Count existing coffee passes in cart
        const existingPasses = prev.filter(c => c.is_coffee_pass);
        
        // Allow multiple passes (for gifting), but show toast about gifting
        if (existingPasses.length >= 1) {
          toast.info('Multiple passes added! Extra passes can be gifted to friends/family.', {
            description: 'The first pass is for you. Additional passes will create gift codes for recipients to claim.',
            duration: 5000
          });
        }
        
        // Add as a new item (don't merge quantities for passes)
        return [...prev, { ...cartItem, pass_number: existingPasses.length + 1 }];
      }
      
      const hasCustomizations = cartItem.modifiers || cartItem.removedIngredients || 
                                cartItem.extraIngredients || cartItem.addOns || cartItem.specialNotes;
      
      // Get size identifier for comparison (compare by name or id, not object reference)
      const getSizeId = (size) => {
        if (!size) return null;
        return size.id || size.name || size;
      };
      
      if (!hasCustomizations) {
        const existing = prev.find(c => 
          c.item_id === cartItem.item_id && 
          getSizeId(c.selectedSize) === getSizeId(cartItem.selectedSize) &&
          !c.modifiers && !c.removedIngredients && !c.extraIngredients && !c.addOns && !c.specialNotes &&
          !c.is_coffee_pass
        );
        
        if (existing) {
          const newQty = existing.quantity + cartItem.quantity;
          if (newQty > settings.max_items_per_order) {
            toast.error(`Maximum ${settings.max_items_per_order} items per order`);
            return prev;
          }
          return prev.map(c => 
            c === existing ? { ...c, quantity: newQty } : c
          );
        }
      }
      
      if (prev.length >= settings.max_items_per_order) {
        toast.error(`Maximum ${settings.max_items_per_order} items per order`);
        return prev;
      }
      
      return [...prev, cartItem];
    });
    
    // Set last added item for upsell popup
    setLastAddedItem(cartItem);
    
    // Show upsell popup if enabled (but not for coffee pass)
    if (settings.upsell_enabled && !cartItem.is_coffee_pass) {
      setShowUpsell(true);
    }
    
    if (settings.play_sounds) {
      try {
        const audio = new Audio('/sounds/add.mp3');
        audio.volume = 0.3;
        audio.play().catch(() => { /* Audio play ignored */ });
      } catch { /* Audio creation ignored */ }
    }
  };

  // Handle adding upsell item directly
  const handleAddUpsellItem = (item) => {
    setCart(prev => [...prev, item]);
    setShowUpsell(false);
    toast.success(`${item.name} added to cart!`);
  };

  // Handle adding free coffee from Coffee Pass redemption
  const handleAddCoffeeClubRedemption = (coffeeItem) => {
    // Check if already has a coffee club redemption in cart
    const hasRedemption = cart.some(item => item.isCoffeeClubRedemption || item.isCoffeePassRedemption);
    if (hasRedemption) {
      toast.error('You can only redeem one free coffee per order');
      return;
    }
    
    setCart(prev => [...prev, coffeeItem]);
    toast.success(coffeeItem.price > 0 
      ? `12oz coffee added (+${coffeeItem.price.toFixed(2)} upgrade)`
      : 'Free 8oz coffee added!'
    );
  };

  // Close upsell popup
  const closeUpsell = () => {
    setShowUpsell(false);
    setLastAddedItem(null);
  };

  // Dismiss idle carousel
  const dismissIdleCarousel = () => {
    setShowIdleCarousel(false);
  };

  // Set valid coupon from coupon input component
  const handleValidCoupon = (info) => {
    setCouponValid(info !== null);
    setCouponInfo(info);
  };

  const updateQuantity = (cartIndex, delta) => {
    setCart(prev => {
      return prev.map((item, idx) => {
        if (idx === cartIndex) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          if (newQty > settings.max_items_per_order) {
            toast.error(`Maximum ${settings.max_items_per_order} items per order`);
            return item;
          }
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean);
    });
  };

  const removeFromCart = (cartIndex) => {
    setCart(prev => prev.filter((_, idx) => idx !== cartIndex));
  };

  const handleLoyaltyLookup = async () => {
    if (!loyaltyLookup.trim()) return;
    
    setLookupLoading(true);
    try {
      // Add 10 second timeout to prevent infinite spinner
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const res = await api.get(`/contacts/search?q=${encodeURIComponent(loyaltyLookup)}`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      setLookupResults(res.data.contacts || []);
      if (res.data.contacts?.length === 0) {
        toast.info("No account found. Would you like to join?");
      }
    } catch (err) {
      console.error('Lookup failed:', err);
      setLookupResults([]);
      
      // Show user-friendly error message
      if (err.name === 'AbortError' || err.code === 'ECONNABORTED') {
        toast.error("Search timed out. Please try again.");
      } else if (err.response?.status === 500) {
        toast.error("Server error. Please try again.");
      } else {
        toast.error("Could not search. Please try again.");
      }
    } finally {
      // Always stop loading - this ensures no infinite spinner
      setLookupLoading(false);
    }
  };

  const handleSelectMember = (member) => {
    setCustomer(member);
    setView('ordering');
    toast.success(`Welcome back, ${member.first_name || member.name || 'Member'}!`);
  };

  const handleSignup = async () => {
    if (!signupForm.first_name || !signupForm.phone) {
      toast.error('Please enter your name and phone number');
      return;
    }
    
    setSignupLoading(true);
    try {
      const fullPhone = `${signupForm.country_code}${signupForm.phone.replace(/^0+/, '')}`;
      
      const res = await api.post('/contacts', {
        first_name: signupForm.first_name,
        last_name: signupForm.last_name,
        email: signupForm.email,
        phone: fullPhone,
        source: 'kiosk_signup',
        loyalty_tier: 'bronze',
        total_points: 0
      });
      
      setCustomer(res.data);
      setView('ordering');
      toast.success(`Welcome to Bulaccino Rewards, ${signupForm.first_name}!`);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Signup failed. Please try again.';
      toast.error(errorMsg);
    }
    setSignupLoading(false);
  };

  const validatePartnerCode = async (code) => {
    if (!code || code.length < 4) {
      setPartnerCodeValid(null);
      setPartnerInfo(null);
      return;
    }
    
    setValidatingCode(true);
    try {
      // Use the new commission-based referral partners endpoint (unified contacts collection)
      const res = await api.get(`/referral-partners/validate/${encodeURIComponent(code)}`);
      
      if (res.data.valid) {
        setPartnerCodeValid(true);
        setPartnerInfo(res.data);
      } else {
        setPartnerCodeValid(false);
        setPartnerInfo(null);
      }
    } catch (err) {
      // Fallback: Check legacy partners collection
      try {
        const legacyRes = await api.get(`/partners/validate/${encodeURIComponent(code)}`);
        if (legacyRes.data.valid) {
          setPartnerCodeValid(true);
          setPartnerInfo(legacyRes.data);
        } else {
          setPartnerCodeValid(false);
          setPartnerInfo(null);
        }
      } catch {
        setPartnerCodeValid(false);
        setPartnerInfo(null);
      }
    }
    setValidatingCode(false);
  };

  // Calculate totals and discounts
  // IMPORTANT: Bula Coffee Pass items are NOT eligible for tier/loyalty discounts
  // Check for BOTH isCoffeeClubRedemption AND isCoffeePassRedemption (modal uses isCoffeePassRedemption)
  const discountableItems = cart.filter(item => !item.isCoffeeClubRedemption && !item.isCoffeePassRedemption);
  const coffeePassItems = cart.filter(item => item.isCoffeeClubRedemption || item.isCoffeePassRedemption);
  
  const discountableSubtotal = discountableItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const coffeePassSubtotal = coffeePassItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartTotal = discountableSubtotal + coffeePassSubtotal;
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const partnerDiscount = partnerCodeValid ? 5 : 0;
  
  // Loyalty tier discount based on customer tier
  const loyaltyTier = customer?.loyalty_tier?.toLowerCase() || 'new_member';
  const loyaltyDiscount = LOYALTY_TIERS[loyaltyTier]?.discount || 0;
  
  // Coupon discount (if valid coupon entered)
  const couponDiscount = couponValid && couponInfo ? couponInfo.discount_percent : 0;
  
  // Total discount (partner + loyalty + coupon, but capped reasonably) - applies ONLY to discountable items
  const totalDiscountPercent = Math.min(partnerDiscount + loyaltyDiscount + couponDiscount, 50);
  const discountAmount = totalDiscountPercent > 0 ? discountableSubtotal * (totalDiscountPercent / 100) : 0;
  const subtotalAfterDiscount = discountableSubtotal - discountAmount + coffeePassSubtotal;
  
  // VAT is INCLUDED in prices (not added on top)
  // Calculate the VAT portion that's already INCLUDED in the total
  // Formula: VAT included = Total / 1.125 × 0.125 (for 12.5% VAT)
  const vatAmount = subtotalAfterDiscount * (vatRate / 100) / (1 + vatRate / 100);
  const finalTotal = subtotalAfterDiscount; // No additional VAT - it's already in the price

  // Category tabs - DYNAMICALLY from API, not hardcoded
  const categoryTabs = useMemo(() => {
    // Use categories from API, fallback to extracting from menu items
    const sourceCategories = menuCategories.length > 0 
      ? menuCategories 
      : [...new Set(menuItems.map(i => i.category))].map(catId => ({
          id: catId,
          label: CATEGORY_LABELS[catId] || catId
        }));
    
    return sourceCategories
      .filter(cat => {
        const hasItems = menuItems.some(i => i.category === cat.id);
        if (!hasItems) return false;
        if (settings.blocked_categories?.includes(cat.id)) return false;
        if (settings.allowed_categories?.length > 0 && !settings.allowed_categories.includes(cat.id)) return false;
        return true;
      })
      .map(cat => ({
        id: cat.id,
        label: cat.label,
        count: menuItems.filter(i => i.category === cat.id).length
      }));
  }, [menuCategories, menuItems, settings.blocked_categories, settings.allowed_categories]);

  // Set default category
  useEffect(() => {
    if (categoryTabs.length > 0 && !categoryTabs.find(c => c.id === activeCategory)) {
      setActiveCategory(categoryTabs[0].id);
    }
  }, [categoryTabs, activeCategory]);

  // Filter items
  const filteredItems = menuItems.filter(item => {
    if (activeCategory !== 'all' && item.category !== activeCategory) return false;
    if (search && !item.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (settings.blocked_categories?.length > 0 && settings.blocked_categories.includes(item.category)) return false;
    if (settings.allowed_categories?.length > 0 && !settings.allowed_categories.includes(item.category)) return false;
    return true;
  });

  // Process card payment
  const processCardPayment = async () => {
    if (cart.length === 0) return;
    
    setProcessingPayment(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const orderData = {
      items: cart.map(item => {
        // Check if this is a coffee pass redemption
        const isCoffeePassRedemption = item.isCoffeeClubRedemption || item.isCoffeePassRedemption || 
                                        item.is_coffee_club_redemption || item.is_coffee_pass_redemption;
        
        // CRITICAL FIX: Ensure Coffee Pass redemptions have correct price ($0 or upgrade cost only)
        let itemPrice = item.price;
        if (isCoffeePassRedemption && itemPrice > 10) {
          // If price > $10 for a redemption, something went wrong - reset to $0
          console.warn(`Coffee pass redemption "${item.name}" has unexpected price $${itemPrice}, resetting to $0`);
          itemPrice = 0;
        }
        
        return {
          item_id: item.item_id,
          name: item.name,
          quantity: item.quantity,
          price: itemPrice,
          category: item.category || null,
          selectedSize: item.selectedSize,
          modifiers: item.modifiers,
          removedIngredients: item.removedIngredients,
          extraIngredients: item.extraIngredients,
          addOns: item.addOns,
          specialNotes: item.specialNotes,
          // Coffee pass flags
          is_coffee_club_redemption: isCoffeePassRedemption,
          isCoffeeClubRedemption: isCoffeePassRedemption,
          isCoffeePassRedemption: isCoffeePassRedemption,
          passRedemptionId: item.passRedemptionId || null
        };
      }),
      customer_name: customer?.first_name ? `${customer.first_name} ${customer.last_name || ''}`.trim() : 'Kiosk Guest',
      customer_id: customer?.contact_id || customer?.id,
      customer_phone: customer?.phone,
      customer_email: customer?.email,
      order_type: 'kiosk',
      total: finalTotal,
      subtotal: cartTotal,
      discount: discountAmount,
      discount_reason: totalDiscountPercent > 0 ? `${partnerDiscount > 0 ? 'Partner ' + partnerDiscount + '%' : ''}${loyaltyDiscount > 0 ? ' Loyalty ' + loyaltyDiscount + '%' : ''}`.trim() : null,
      status: 'pending_confirmation',
      payment_status: 'paid',
      payment_method: 'mpaisa',
      partner_code: partnerCodeValid ? partnerCode : null
    };

    try {
      const res = await api.post('/orders', orderData);
      const order = res.data;
      setOrderNumber(order.order_number || order.id?.slice(0, 8).toUpperCase());
      setPaymentStatus('paid');  // Mark as paid for M-PAiSA/Card
      
      // PRINT TO KIOSK PRINTER - Customer takes this receipt to cashier for verification
      try {
        await api.post(`/receipts/kiosk-print?order_id=${order.id || order.order_id}`);
        console.log('Kiosk receipt printed for M-PAiSA paid order');
      } catch (printErr) {
        console.error('Kiosk receipt print failed:', printErr);
        // Don't block order flow if print fails
      }
      
      setView('confirmation');
      
      if (customer?.contact_id || customer?.id) {
        try {
          await api.post('/loyalty/award-points', {
            contact_id: customer.contact_id || customer.id,
            points: Math.floor(finalTotal),
            reason: `Kiosk Order ${order.order_number || order.id?.slice(0, 8)}`
          });
        } catch (e) {
          console.error('Failed to award loyalty points:', e);
        }
      }
      
      // Note: Auto-reset is now handled by KioskConfirmation component with visible countdown
      // setTimeout(resetKiosk, 15000);  // Removed - countdown in confirmation component handles this
    } catch (err) {
      toast.error('Order failed. Please try again.');
    }
    
    setProcessingPayment(false);
  };

  const value = {
    // View
    view, setView,
    // Menu & Cart
    menuItems, cart, loading, activeCategory, setActiveCategory,
    search, setSearch, orderNumber, setOrderNumber, availableAddOns,
    // Customer
    customer, setCustomer, loyaltyLookup, setLoyaltyLookup,
    lookupResults, lookupLoading,
    // Signup
    signupForm, setSignupForm, signupLoading,
    // Customization
    selectedItem, setSelectedItem, showCustomization, setShowCustomization,
    // Partner
    partnerCode, setPartnerCode, partnerCodeValid, partnerInfo, validatingCode,
    // Coupon
    couponCode, setCouponCode, couponValid, couponInfo, handleValidCoupon,
    // Coffee Pass
    coffeeClubStatus, coffeeClubRedemptionWindow, fetchCoffeeClubStatus, setCoffeeClubStatus,
    // Payment
    processingPayment,
    // Payment status
    paymentStatus, setPaymentStatus,
    // Upsell
    showUpsell, closeUpsell, lastAddedItem, handleAddUpsellItem,
    // Idle Carousel
    showIdleCarousel, dismissIdleCarousel,
    // Settings
    settings,
    // Accessibility
    isAccessible, accessibilityEnabled, setAccessibilityEnabled,
    buttonSizeClass, iconButtonClass, iconClass, largeIconClass,
    // Computed
    cartTotal, cartCount, partnerDiscount, loyaltyDiscount, couponDiscount,
    totalDiscountPercent, discountAmount, subtotalAfterDiscount,
    vatRate, vatAmount, finalTotal,
    categoryTabs, filteredItems,
    // Actions
    resetKiosk, handleItemClick, handleAddCustomizedItem,
    updateQuantity, removeFromCart, handleLoyaltyLookup,
    handleSelectMember, handleSignup, processCardPayment,
    handleAddCoffeeClubRedemption
  };

  return (
    <KioskContext.Provider value={value}>
      {children}
    </KioskContext.Provider>
  );
}

export function useKiosk() {
  const context = useContext(KioskContext);
  if (!context) {
    throw new Error('useKiosk must be used within a KioskProvider');
  }
  return context;
}
